import { DXF_CO_APPLICABLE_ROLES, type DxfMappingRole } from '@frontend/api';
import { Box } from '@frontend/legacy-components';

import { useDxfViewerSession } from 'modules/common/viewer/dxfViewer/DxfViewerContext';
import { DXF_Z_INDEX } from 'modules/common/viewer/dxfViewer/constants';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

import { useDxfMappingMenuAnchor } from '../hooks/useDxfMappingMenuAnchor';
import { DxfRoleActions } from './DxfRoleActions';

/**
 * The role flyout that appears over a selection while mapping.
 *
 * Visibility is `session.roleChrome.kind === 'flyout'` — exclusive with the
 * context menu, and hidden when the selection is empty, without this component
 * naming either.
 *
 * Position is written directly to the DOM by `useDxfMappingMenuAnchor` so the menu
 * tracks pan and zoom without re-rendering; only its contents go through React.
 *
 * Role picks toggle on/off like `DxfMappingContextMenu`, and co-applied pairs
 * (etch + bend) highlight every role the selection shares.
 */
export const DxfMappingMenu = () => {
  const session = useDxfViewerSession();
  const chrome = useReadableStore(session.roleChrome);
  const activeRoles = useReadableStore(session.mapping.selectionSharedRoles);
  const lockReason = useReadableStore(
    session.mapping,
    (current) => current.lockReason,
  );

  const isOpen = chrome.kind === 'flyout';
  const menuRef = useDxfMappingMenuAnchor({
    viewport: session.viewport,
    selection: session.selection,
    model: session.model,
    isOpen,
  });

  const handlePick = (role: DxfMappingRole) => {
    if (DXF_CO_APPLICABLE_ROLES.has(role)) {
      session.mapping.toggleRoleOnSelection(role);
      return;
    }
    session.mapping.toggleRoleOnSelectionAndClear(role);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      ref={menuRef}
      position="absolute"
      top={0}
      left={0}
      zIndex={DXF_Z_INDEX.FLOATING}
      // Placed by the anchor hook before the first paint; without this the menu
      // would flash at the container's top-left corner on the frame it mounts.
      visibility="hidden"
      bg="page.surface"
      border="1px solid"
      borderColor="separator.border"
      borderRadius="lg"
      boxShadow="popover"
      p={0.75}
      pointerEvents="auto"
      data-testid="dxf-mapping-menu"
    >
      <DxfRoleActions
        activeRoles={activeRoles}
        onPick={handlePick}
        isDisabled={lockReason !== undefined}
      />
    </Box>
  );
};
DxfMappingMenu.displayName = 'DxfMappingMenu';
